import { useEffect, useState } from 'react'
import { Alert, App, Button, Flex, Space, Spin, Switch, Typography } from 'antd'
import { InstagramOutlined } from '@ant-design/icons'
import {
  createProfile,
  getConnectUrlForInstagram,
} from '../../store/ProfileReducer'
import SBInstagramConnect from '../../modules/authorization-modal/connect'
import { AuthorizationResultCode } from '../../modules/authorization-modal/types'
import { useAppDispatch } from '../../store/hooks'
import { AxiosError } from 'axios'
import { APIResponse, MessageResponse } from '../../models/helper'
import i18n from '../../i18n/setupLocalization'
import LocalizationKeys from '../../i18n/LocalizationKeys'
import { ProfileSummary } from '../../models/Profile'
import { Profile } from './Profile'
import { useHypertune } from '../../generated/hypertune.react'
import { Link } from 'react-router-dom'
import { Trans } from 'react-i18next'

const { Text, Title } = Typography

interface Props {
  onCreated: () => void
}

export const ProfileConnectFlow = ({ onCreated }: Props) => {
  const dispatch = useAppDispatch()
  const { notification } = App.useApp()
  const hypertune = useHypertune()
  const [isOptin, setIsOptin] = useState<boolean>(false)
  const [step, setStep] = useState<'instagram' | 'pricing'>('instagram')
  const [instagramStatus, setInstagramStatus] = useState<
    'loading' | 'ready' | 'awaiting_instagram' | 'creating' | 'error'
  >('ready')
  const [connect, setConnect] = useState<SBInstagramConnect | undefined>(
    undefined
  )
  const [profileSummary, setProfileSummary] = useState<
    ProfileSummary | undefined
  >(undefined)
  const [startHasBeenLogged, setStartHasBeenLogged] = useState<boolean>(false)

  const startInstagramFlow = () => {
    if (connect === undefined) return
    setInstagramStatus('awaiting_instagram')

    connect
      .trigger()
      .then((response) => {
        if (
          response.eventType !==
            AuthorizationResultCode.AUTHORIZATION_SUCEEDED ||
          !response.data.code
        ) {
          return
        }
        setInstagramStatus('creating')
        hypertune.instagramConnectInstagramSuccess()

        // Create profile on API
        dispatch(createProfile(response.data.code))
          .then((response: APIResponse<ProfileSummary>) => {
            // Log success
            hypertune.instagramConnectCreationSuccess()

            setProfileSummary(response.data)

            // Update step
            setStep('pricing')

            // Reset instagram button
            setInstagramStatus('ready')
          })
          .catch((error: AxiosError) => {
            setInstagramStatus('error')
            const data = error.response?.data as APIResponse<MessageResponse>
            notification.error({
              message: i18n.t(
                LocalizationKeys.Component.Profile.AddCard.RegisterError
              ),
              description: i18n.t(
                i18n.t(
                  LocalizationKeys.Component.Profile.AddCard[
                    data &&
                    data.data &&
                    data.data.message === 'profiles.already_connected'
                      ? 'RegisterErrorDetailsAlreadyConnected'
                      : 'RegisterErrorDetails'
                  ]
                )
              ),
              duration: 0,
            })
          })
      })
      .catch((error) => {
        setInstagramStatus('error')
      })
  }

  const initConnect = () => {
    setInstagramStatus('loading')
    setConnect(undefined)
    getConnectUrlForInstagram()
      .then((response) => {
        setConnect(new SBInstagramConnect(response.url))
        setInstagramStatus('ready')
      })
      .catch(() => {
        setInstagramStatus('error')
      })
  }

  useEffect(() => {
    initConnect()

    return () => {
      setInstagramStatus('loading')
      setConnect(undefined)
      setStartHasBeenLogged(false)
    }
  }, [])

  useEffect(() => {
    if (!hypertune.isReady() || startHasBeenLogged) {
      return
    }
    hypertune.instagramConnectStarts()
    setStartHasBeenLogged(true)
  }, [hypertune])

  return (
    <>
      {step === 'instagram' && (
        <Flex
          gap={20}
          vertical
          justify="center"
          align="center"
          className="w-full"
        >
          <Space
            direction="vertical"
            style={{ maxWidth: 600, textAlign: 'center' }}
          >
            <img src="/assets/images/connect.png" width={200} />
            <Title level={3}>
              {i18n.t(
                LocalizationKeys.Component.Profile.ConnectFlow
                  .AddInstagramAccount
              )}
            </Title>
            <Text>
              {i18n.t(
                LocalizationKeys.Component.Profile.ConnectFlow
                  .InstagramConnection
              )}
            </Text>
          </Space>
          <Flex vertical align="center" gap={20} style={{ maxWidth: 400 }}>
            <Space size={10}>
              <Switch
                value={isOptin}
                onChange={(newValue) => setIsOptin(newValue)}
              />
              <Text type="secondary">
                <Trans
                  i18nKey={
                    LocalizationKeys.Component.Profile.ConnectFlow.CheckMention
                  }
                  components={[
                    <Link key={0} target="_blank" to="/legal/cgu" />,
                  ]}
                />
              </Text>
            </Space>
            {instagramStatus === 'ready' && (
              <Button
                size="large"
                color="default"
                style={{
                  border: 0,
                  background:
                    instagramStatus !== 'ready' || !isOptin
                      ? '#dedede'
                      : 'linear-gradient(45deg, rgba(68,101,201,1) 0%, rgba(162,49,192,1) 25%, rgba(249,81,73,1) 51%, rgba(253,220,87,1) 100%)',
                }}
                disabled={instagramStatus !== 'ready' || !isOptin}
                onClick={() => {
                  if (!isOptin || instagramStatus !== 'ready') return
                  hypertune.instagramConnectButtonClicked()
                  startInstagramFlow()
                }}
              >
                <Space size={5} style={{ color: 'white' }}>
                  <InstagramOutlined />
                  <Text
                    style={{ padding: 10, fontWeight: 600, color: 'white' }}
                  >
                    {i18n.t(
                      LocalizationKeys.Component.Profile.ConnectFlow
                        .LoginWithInstagram
                    )}
                  </Text>
                </Space>
              </Button>
            )}
          </Flex>
          {(instagramStatus === 'loading' ||
            instagramStatus === 'awaiting_instagram' ||
            instagramStatus === 'creating') && (
            <Space size={5}>
              <Spin />
            </Space>
          )}
          {instagramStatus === 'error' && (
            <Alert
              type="error"
              message={
                <Text style={{ marginRight: 10 }}>
                  {i18n.t(
                    LocalizationKeys.Component.Profile.AddCard.ConnectError
                  )}
                </Text>
              }
              action={
                <Button onClick={initConnect}>
                  {i18n.t(LocalizationKeys.Component.Profile.AddCard.TryAgain)}
                </Button>
              }
            />
          )}
        </Flex>
      )}
      {step === 'pricing' && profileSummary && (
        <Flex vertical style={{ textAlign: 'center', width: '100%' }} gap={20}>
          <Space direction="vertical">
            <Title level={3} className="m-0">
              {i18n.t(LocalizationKeys.Component.Profile.ConnectFlow.Welcome)}
            </Title>
            <Text>
              {i18n.t(
                LocalizationKeys.Component.Profile.ConnectFlow.ChoosePlan
              )}
            </Text>
          </Space>
          <Profile.PricingTable profileSlug={profileSummary.slug} />
        </Flex>
      )}
    </>
  )
}

export type ProfileConnectFlowType = { ConnectFlow: typeof ProfileConnectFlow }
